/**
 * StorageWrapper provides a fallback mechanism to IndexedDB when localStorage is full.
 * It maintains the same API as localStorage but handles the fallback transparently.
 */

interface StorageResult<T> {
  success: boolean;
  data?: T;
  method?: 'localStorage' | 'indexedDB';
  error?: any;
}

class StorageWrapper {
  private static instance: StorageWrapper;
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<IDBDatabase> | null = null;
  private readonly DB_NAME = 'HVGAStorage';
  private readonly STORE_NAME = 'localStorage';
  private readonly DB_VERSION = 1;

  private constructor() {
    // Private constructor for singleton pattern
  }

  public static getInstance(): StorageWrapper {
    if (!StorageWrapper.instance) {
      StorageWrapper.instance = new StorageWrapper();
    }
    return StorageWrapper.instance;
  }

  /**
   * Initialize IndexedDB
   */
  private async initDB(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      try {
        const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

        request.onerror = () => {
          console.error('Failed to open IndexedDB:', request.error);
          reject(request.error);
        };

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains(this.STORE_NAME)) {
            db.createObjectStore(this.STORE_NAME);
          }
        };

        request.onsuccess = (event) => {
          this.db = (event.target as IDBOpenDBRequest).result;
          resolve(this.db);
        };
      } catch (error) {
        console.error('Error initializing IndexedDB:', error);
        reject(error);
      }
    });

    return this.dbPromise;
  }

  /**
   * Save data with localStorage, falling back to IndexedDB if localStorage is full
   */
  public async setItem<T>(key: string, data: T): Promise<StorageResult<T>> {
    const value = JSON.stringify(data);

    try {
      // Try localStorage first
      localStorage.setItem(key, value);
      return { success: true, method: 'localStorage' };
    } catch (e) {
      console.warn(`localStorage failed for ${key}, trying IndexedDB`);

      try {
        const db = await this.initDB();
        await new Promise<void>((resolve, reject) => {
          const transaction = db.transaction([this.STORE_NAME], 'readwrite');
          const store = transaction.objectStore(this.STORE_NAME);
          const request = store.put(value, key);

          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });

        return { success: true, method: 'indexedDB' };
      } catch (error) {
        console.error(`All storage methods failed for ${key}:`, error);
        return { success: false, error };
      }
    }
  }

  /**
   * Get data from localStorage, falling back to IndexedDB if not found
   */
  public async getItem<T>(key: string): Promise<StorageResult<T>> {
    try {
      // Try localStorage first
      const value = localStorage.getItem(key);
      if (value !== null) {
        return { success: true, data: JSON.parse(value), method: 'localStorage' };
      }
    } catch (e) {
      console.warn(`localStorage failed for ${key}, trying IndexedDB`);
    }

    // If localStorage fails or returns null, try IndexedDB
    try {
      const db = await this.initDB();
      const value = await new Promise<string>((resolve, reject) => {
        const transaction = db.transaction([this.STORE_NAME], 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (value) {
        return { success: true, data: JSON.parse(value), method: 'indexedDB' };
      }
    } catch (error) {
      console.error(`All storage methods failed for ${key}:`, error);
    }

    return { success: false, data: null as any };
  }

  /**
   * Remove item from both localStorage and IndexedDB
   */
  public async removeItem(key: string): Promise<StorageResult<void>> {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`localStorage.removeItem failed for ${key}`);
    }

    try {
      const db = await this.initDB();
      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.delete(key);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      return { success: true };
    } catch (error) {
      console.error(`Failed to remove item from IndexedDB: ${key}`, error);
      return { success: false, error };
    }
  }

  /**
   * Clear all data from both localStorage and IndexedDB
   */
  public async clear(): Promise<StorageResult<void>> {
    try {
      localStorage.clear();
    } catch (e) {
      console.warn('localStorage.clear failed');
    }

    try {
      const db = await this.initDB();
      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      return { success: true };
    } catch (error) {
      console.error('Failed to clear IndexedDB:', error);
      return { success: false, error };
    }
  }

  /**
   * Get all keys from both localStorage and IndexedDB
   */
  public async getAllKeys(): Promise<string[]> {
    const keys = new Set<string>();

    // Get keys from localStorage
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) keys.add(key);
      }
    } catch (e) {
      console.warn('Failed to get keys from localStorage');
    }

    // Get keys from IndexedDB
    try {
      const db = await this.initDB();
      const idbKeys = await new Promise<string[]>((resolve, reject) => {
        const transaction = db.transaction([this.STORE_NAME], 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.getAllKeys();

        request.onsuccess = () => resolve(request.result as string[]);
        request.onerror = () => reject(request.error);
      });

      idbKeys.forEach(key => keys.add(key));
    } catch (error) {
      console.error('Failed to get keys from IndexedDB:', error);
    }

    return Array.from(keys);
  }

  /**
   * Check if a key exists in either storage
   */
  public async hasKey(key: string): Promise<boolean> {
    try {
      if (localStorage.getItem(key) !== null) {
        return true;
      }
    } catch (e) {
      console.warn(`localStorage check failed for ${key}`);
    }

    try {
      const db = await this.initDB();
      const exists = await new Promise<boolean>((resolve, reject) => {
        const transaction = db.transaction([this.STORE_NAME], 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.count(key);

        request.onsuccess = () => resolve(request.result > 0);
        request.onerror = () => reject(request.error);
      });

      return exists;
    } catch (error) {
      console.error(`Failed to check key in IndexedDB: ${key}`, error);
      return false;
    }
  }

  /**
   * Get storage usage statistics
   */
  public async getStorageStats(): Promise<{
    localStorage: { used: number; keys: number };
    indexedDB: { used: number; keys: number };
  }> {
    const stats = {
      localStorage: { used: 0, keys: 0 },
      indexedDB: { used: 0, keys: 0 }
    };

    // Get localStorage stats
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          const value = localStorage.getItem(key);
          if (value) {
            stats.localStorage.used += value.length * 2; // Approximate size in bytes
            stats.localStorage.keys++;
          }
        }
      }
    } catch (e) {
      console.warn('Failed to get localStorage stats');
    }

    // Get IndexedDB stats
    try {
      const db = await this.initDB();
      const transaction = db.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      
      // Get all keys first
      const keys = await new Promise<string[]>((resolve, reject) => {
        const request = store.getAllKeys();
        request.onsuccess = () => resolve(request.result as string[]);
        request.onerror = () => reject(request.error);
      });

      // Get all values
      const values = await new Promise<any[]>((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      stats.indexedDB.keys = keys.length;
      stats.indexedDB.used = values.reduce((total, value) => {
        return total + (JSON.stringify(value).length * 2); // Approximate size in bytes
      }, 0);
    } catch (error) {
      console.error('Failed to get IndexedDB stats:', error);
    }

    return stats;
  }
}

// Export singleton instance
export const storageWrapper = StorageWrapper.getInstance(); 